package org.wordsapp.ui.screen

import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.wordsapp.ui.component.CompletionView
import org.wordsapp.ui.component.PrimaryButton
import org.wordsapp.ui.component.RoundProgressBar
import org.wordsapp.viewmodel.LastResult
import org.wordsapp.viewmodel.WordViewModel

private const val SHOW_DEBUG_CONTROLS = false

@Composable
fun PracticeScreen(
    viewModel: WordViewModel,
    onNavigateToStatistics: () -> Unit,
) {
    val currentWord by viewModel.currentWord.collectAsState()
    val lastResult by viewModel.lastResult.collectAsState()
    val roundCompleted by viewModel.roundCompleted.collectAsState()
    val isLoading by viewModel.isLoading.collectAsState()
    val totalRoundWords by viewModel.totalRoundWords.collectAsState()
    val answeredInRound by viewModel.answeredInRound.collectAsState()
    val errorMessage by viewModel.errorMessage.collectAsState()

    var userAnswer by remember { mutableStateOf("") }
    val focusRequester = remember { FocusRequester() }

    if (roundCompleted) {
        CompletionView(
            totalWords = totalRoundWords,
            onNextRound = {
                userAnswer = ""
                viewModel.startNewRound()
            },
            onViewStatistics = onNavigateToStatistics,
        )
        return
    }

    Column(
        modifier = Modifier
            .fillMaxSize()
            .padding(16.dp),
    ) {
        RoundProgressBar(
            answered = answeredInRound,
            total = totalRoundWords,
        )

        Spacer(Modifier.height(24.dp))

        if (isLoading) {
            Box(
                modifier = Modifier.fillMaxSize(),
                contentAlignment = Alignment.Center,
            ) {
                CircularProgressIndicator()
            }
        } else if (errorMessage != null) {
            Box(
                modifier = Modifier.fillMaxSize(),
                contentAlignment = Alignment.Center,
            ) {
                Column(horizontalAlignment = Alignment.CenterHorizontally) {
                    Text(
                        text = errorMessage ?: "",
                        color = MaterialTheme.colorScheme.error,
                        textAlign = TextAlign.Center,
                    )
                    Spacer(Modifier.height(16.dp))
                    PrimaryButton(
                        text = "Retry",
                        onClick = {
                            viewModel.clearError()
                            viewModel.loadWords()
                        },
                    )
                }
            }
        } else {
            currentWord?.let { word ->
                Card(
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(16.dp),
                    elevation = CardDefaults.cardElevation(defaultElevation = 2.dp),
                ) {
                    Column(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(24.dp),
                        horizontalAlignment = Alignment.CenterHorizontally,
                    ) {
                        Text(
                            text = "Translate to German:",
                            style = MaterialTheme.typography.labelLarge,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                        )
                        Spacer(Modifier.height(16.dp))
                        Text(
                            text = word.translation,
                            fontSize = 28.sp,
                            fontWeight = FontWeight.Bold,
                            textAlign = TextAlign.Center,
                        )
                    }
                }

                Spacer(Modifier.height(24.dp))

                LaunchedEffect(word.id) {
                    focusRequester.requestFocus()
                }
                OutlinedTextField(
                    value = userAnswer,
                    onValueChange = { userAnswer = it },
                    modifier = Modifier.fillMaxWidth().focusRequester(focusRequester),
                    label = { Text("Your answer") },
                    singleLine = true,
                    shape = RoundedCornerShape(12.dp),
                    keyboardOptions = KeyboardOptions(imeAction = ImeAction.Done),
                    keyboardActions = KeyboardActions(
                        onDone = {
                            if (userAnswer.isNotBlank()) {
                                if (viewModel.checkAnswer(userAnswer)) {
                                    userAnswer = ""
                                }
                            }
                        },
                    ),
                )
            }

            lastResult?.let { result ->
                Spacer(Modifier.height(16.dp))
                ResultBanner(result)
            }

            Spacer(Modifier.height(16.dp))
            PrimaryButton(
                text = "Check",
                onClick = {
                    if (viewModel.checkAnswer(userAnswer)) {
                        userAnswer = ""
                    }
                },
                enabled = userAnswer.isNotBlank(),
            )

            if (SHOW_DEBUG_CONTROLS) {
                Spacer(Modifier.height(4.dp))
                TextButton(
                    onClick = { viewModel.forceCompleteRound() },
                    modifier = Modifier.align(Alignment.CenterHorizontally),
                ) {
                    Text("Force complete (debug)")
                }
            }
        }
    }
}

@Composable
private fun ResultBanner(result: LastResult) {
    val containerColor = when {
        result.missingArticle -> MaterialTheme.colorScheme.tertiaryContainer
        result.correct -> MaterialTheme.colorScheme.primaryContainer
        else -> MaterialTheme.colorScheme.errorContainer
    }
    val textColor = when {
        result.missingArticle -> MaterialTheme.colorScheme.onTertiaryContainer
        result.correct -> MaterialTheme.colorScheme.onPrimaryContainer
        else -> MaterialTheme.colorScheme.onErrorContainer
    }
    val title = when {
        result.missingArticle -> "What's the article?"
        result.correct -> "Correct!"
        else -> "Incorrect"
    }

    Card(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(12.dp),
        colors = CardDefaults.cardColors(containerColor = containerColor),
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(16.dp),
        ) {
            Text(
                text = title,
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.Bold,
                color = textColor,
            )
            if (result.missingArticle) {
                Spacer(Modifier.height(4.dp))
                Text(
                    text = "der, die, or das?",
                    style = MaterialTheme.typography.bodyLarge,
                    color = textColor,
                )
            } else if (!result.correct) {
                Spacer(Modifier.height(4.dp))
                Text(
                    text = "${result.translation} - ${result.foreignWord}",
                    style = MaterialTheme.typography.bodyLarge,
                    color = textColor,
                )
            }
            if (result.spellingHint != null) {
                Spacer(Modifier.height(4.dp))
                Text(
                    text = "Correct spelling: ${result.spellingHint}",
                    style = MaterialTheme.typography.bodyMedium,
                    color = textColor,
                )
            }
        }
    }
}
